import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import {
  makeKiddomGetRequest,
  makeKiddomMultipartPostRequest,
} from 'infrastructure/api'
import { exportCsv } from 'shared/utils'
import { CSVTask, defaultUploadPathByTask } from './CSVUpload.config'
import type {
  CSVColumnResponse,
  CSVUploadSummary,
  CSVUploadSummaryResponse,
} from './CSVUpload.types'
import {
  buildPathWithQuery,
  buildTableHeaders,
  normalizeSummary,
} from './CSVUpload.utils'

interface UseCSVUploadParams {
  initialTask?: CSVTask
  schoolID?: number
}

/**
 * Manages CSV upload dialog state, column loading, template export, and upload workflow.
 * @param params - Initial task and optional school scope for create-user uploads
 * @returns State and handlers used by the CSV upload UI
 */
export function useCSVUpload({ initialTask, schoolID }: UseCSVUploadParams) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState<CSVTask | ''>(
    initialTask ?? '',
  )
  const [isLoadingColumns, setIsLoadingColumns] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [columnResponse, setColumnResponse] = useState<CSVColumnResponse>()
  const [summary, setSummary] = useState<CSVUploadSummary | null>(null)
  const [addRoles, setAddRoles] = useState(true)
  const [fileName, setFileName] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const resolvedUploadPaths = useMemo(() => {
    const basePaths = { ...defaultUploadPathByTask }

    if (schoolID !== undefined) {
      basePaths.createUser = `/v1/kat/schools/${schoolID}/csv/create`
    }

    return basePaths
  }, [schoolID])

  const selectedUploadPath =
    selectedTask !== '' ? (resolvedUploadPaths[selectedTask] ?? '') : ''
  const selectedQueryParams =
    selectedTask === 'updateUser' ? { add_roles: addRoles } : {}
  const tableHeaders =
    selectedTask !== '' ? buildTableHeaders(columnResponse) : []
  const inputId = useMemo(
    () =>
      `csv-task-upload-input-${selectedTask}-${selectedUploadPath.replace(/\W/g, '-')}`,
    [selectedTask, selectedUploadPath],
  )

  useEffect(() => {
    if (!isDialogOpen) {
      return
    }

    setErrorMessage(null)
    setSummary(null)

    if (selectedTask === '') {
      setColumnResponse(undefined)
      setIsLoadingColumns(false)
      return
    }

    let mounted = true
    setIsLoadingColumns(true)

    makeKiddomGetRequest<CSVColumnResponse>({
      kiddomPath: buildPathWithQuery('/v1/kat/csv/columns', {
        csv_task: selectedTask,
      }),
    })
      .then(({ result, error }) => {
        if (!mounted) {
          return
        }

        if (error != null) {
          setErrorMessage(error)
          return
        }

        setColumnResponse(result)
      })
      .finally(() => {
        if (mounted) {
          setIsLoadingColumns(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [selectedTask, isDialogOpen])

  const openDialog = () => {
    setIsDialogOpen(true)
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    setErrorMessage(null)
    setSummary(null)
    setFileName('')
  }

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]

    if (!selectedFile) {
      return
    }

    setSummary(null)
    setErrorMessage(null)
    setFileName(selectedFile.name)

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setErrorMessage('Please choose a file with a .csv extension.')
      event.target.value = ''
      return
    }

    if (!selectedUploadPath) {
      setErrorMessage(
        `Upload endpoint is not configured for task: ${selectedTask}`,
      )
      event.target.value = ''
      return
    }

    const formData = new FormData()
    formData.append('file', selectedFile, selectedFile.name)
    setIsUploading(true)

    const { result, error } =
      await makeKiddomMultipartPostRequest<CSVUploadSummaryResponse>({
        kiddomPath: buildPathWithQuery(selectedUploadPath, selectedQueryParams),
        formData,
      })

    if (error != null) {
      setErrorMessage(error)
    } else {
      setSummary(normalizeSummary(result))
    }

    setIsUploading(false)
    event.target.value = ''
  }

  const onDownloadTemplate = () => {
    if (tableHeaders.length === 0 || selectedTask === '') {
      return
    }

    exportCsv(
      [tableHeaders.map(({ name }) => name)],
      `${selectedTask}-template`,
    )
  }

  return {
    isDialogOpen,
    selectedTask,
    isLoadingColumns,
    isUploading,
    addRoles,
    fileName,
    errorMessage,
    summary,
    inputId,
    tableHeaders,
    openDialog,
    closeDialog,
    onTaskChange: setSelectedTask,
    onAddRolesChange: setAddRoles,
    onFileChange,
    onDownloadTemplate,
  }
}
